import { useLayoutEffect, useState } from 'react';
import type { RefObject } from 'react';
import { getTelegramWebApp } from '@/hooks/useTelegram';
import { clipToAppChrome, getVisibleArea } from '@/utils/viewportBand';

/**
 * Fixed-position box (viewport px) of a panel under the header; its bottom is
 * the layout-viewport bottom (CSS `bottom: 0`), so it is not measured here.
 */
export interface OverlayBox {
  top: number;
  left: number;
  width: number;
  /** Part of the box covered by the keyboard and/or the visible BottomNav. */
  bottomInset: number;
}

const EMPTY_BOX: OverlayBox = { top: 0, left: 0, width: 0, bottomInset: 0 };

const sameBox = (a: OverlayBox, b: OverlayBox): boolean =>
  a.top === b.top && a.left === b.left && a.width === b.width && a.bottomInset === b.bottomInset;

const measureBox = (anchor: HTMLElement): OverlayBox => {
  const rect = anchor.getBoundingClientRect();
  const { bottom } = clipToAppChrome(getVisibleArea());
  return {
    top: rect.bottom,
    left: rect.left,
    width: rect.width,
    bottomInset: Math.max(0, window.innerHeight - bottom),
  };
};

/**
 * Box from the bottom of `anchorRef` (the header) down to the layout-viewport
 * bottom, horizontally matching the anchor, so on desktop a panel never covers
 * the sidebar. The box does not depend on the keyboard — hiding it never
 * uncovers the page below. What the keyboard (`visualViewport`) or the visible
 * BottomNav covers is `bottomInset`, for the panel to keep its content clear
 * of. Follows the keyboard, viewport and Telegram viewport changes; never
 * derived from `viewportHeight`.
 */
export function useOverlayBox(anchorRef: RefObject<HTMLElement>): OverlayBox {
  const [box, setBox] = useState<OverlayBox>(EMPTY_BOX);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return undefined;

    let frame = 0;
    const update = () => {
      const next = measureBox(anchor);
      setBox((prev) => (sameBox(prev, next) ? prev : next));
    };
    const schedule = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    };

    update();
    const tg = getTelegramWebApp();
    const vv = window.visualViewport;
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, { passive: true });
    vv?.addEventListener('resize', schedule);
    vv?.addEventListener('scroll', schedule);
    tg?.onEvent('viewportChanged', schedule);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule);
      vv?.removeEventListener('resize', schedule);
      vv?.removeEventListener('scroll', schedule);
      tg?.offEvent('viewportChanged', schedule);
    };
  }, [anchorRef]);

  return box;
}
